import { useTranslation } from 'react-i18next'

/**
 * Lightweight client-side strength hint (UX only). The server is authoritative:
 * it enforces zxcvbn score >= 3 on register/reset. Kept dependency-free to avoid
 * shipping the full zxcvbn dictionary to the browser.
 */
function scorePassword(pw: string): number {
  let score = 0
  if (pw.length >= 12) score += 1
  if (pw.length >= 16) score += 1
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1
  if (/\d/.test(pw)) score += 1
  if (/[^A-Za-z0-9]/.test(pw)) score += 1
  if (/^(.)\1+$/.test(pw) || /(1234|password|qwerty|azerty)/i.test(pw)) score = Math.min(score, 1)
  return Math.min(score, 4)
}

export function PasswordStrengthMeter({ password }: { password: string }) {
  const { t } = useTranslation('auth')
  if (!password) return null

  const score = scorePassword(password)
  const labels = [
    t('password_strength.very_weak'),
    t('password_strength.weak'),
    t('password_strength.fair'),
    t('password_strength.good'),
    t('password_strength.strong'),
  ]

  return (
    <div className="mt-2" aria-live="polite" aria-label={t('password_strength.label')}>
      <div className="flex gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={['h-1 flex-1 rounded-full', i < score ? 'bg-text' : 'bg-border'].join(' ')}
          />
        ))}
      </div>
      <p className="mt-1 text-xs text-text-muted">{labels[score]}</p>
    </div>
  )
}
